import React, { useState, useEffect } from 'react';
import './SuppliesStock.css';

function SuppliesStock() {
  const [suppliesData, setSuppliesData] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [editingHomeStock, setEditingHomeStock] = useState(null);
  const [editingTransitStock, setEditingTransitStock] = useState(null);
  const [tempHomeStock, setTempHomeStock] = useState('');
  const [tempTransitStock, setTempTransitStock] = useState('');
  const [tempTransitDate, setTempTransitDate] = useState('');
  const [tempSupplierId, setTempSupplierId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Charger les données sauvegardées au démarrage
  useEffect(() => {
    const savedSupplies = localStorage.getItem('suppliesStock');
    if (savedSupplies) {
      try {
        setSuppliesData(JSON.parse(savedSupplies));
      } catch (e) {
        console.error('Erreur lors du chargement du stock fournitures :', e);
      }
    }
  }, []);

  // Sauvegarder les données à chaque modification
  const saveSuppliesData = (data) => {
    localStorage.setItem('suppliesStock', JSON.stringify(data));
  };

  // Ajouter un nouvel article
  const addNewItem = () => {
    if (newItemName.trim() === '') return;
    
    const newItem = {
      id: Date.now(),
      name: newItemName.trim(),
      homeQuantity: 0,
      transitQuantity: 0,
      transitOrderDate: null,
      transitSupplierId: null
    };
    
    const updatedData = [...suppliesData, newItem];
    setSuppliesData(updatedData);
    saveSuppliesData(updatedData);
    setNewItemName('');
  };

  // Supprimer un article
  const removeItem = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      const updatedData = suppliesData.filter(item => item.id !== id);
      setSuppliesData(updatedData);
      saveSuppliesData(updatedData);
    }
  };

  // Gestion de l'édition du stock à la maison
  const handleHomeStockEdit = (id) => {
    const item = suppliesData.find(item => item.id === id);
    setEditingHomeStock(id);
    setTempHomeStock(item.homeQuantity || 0);
  };

  const handleHomeStockSave = (id) => {
    const updatedData = suppliesData.map(item => 
      item.id === id 
        ? { ...item, homeQuantity: parseInt(tempHomeStock) || 0 }
        : item
    );
    setSuppliesData(updatedData);
    saveSuppliesData(updatedData);
    setEditingHomeStock(null);
  };

  // Gestion de l'édition du stock en transit
  const handleTransitStockEdit = (id) => {
    const item = suppliesData.find(item => item.id === id);
    
    let orderDate = new Date();
    if (item.transitOrderDate) {
      orderDate = new Date(item.transitOrderDate);
    }
    
    setEditingTransitStock(id);
    setTempTransitStock(item.transitQuantity || 0);
    setTempTransitDate(orderDate.toISOString().split('T')[0]);
    setTempSupplierId(item.transitSupplierId || '');
  };

  const handleTransitStockSave = (id) => {
    const updatedData = suppliesData.map(item => 
      item.id === id 
        ? { 
            ...item, 
            transitQuantity: parseInt(tempTransitStock) || 0,
            transitOrderDate: tempTransitDate || null,
            transitSupplierId: tempSupplierId || null
          }
        : item
    );
    setSuppliesData(updatedData);
    saveSuppliesData(updatedData);
    setEditingTransitStock(null);
  };

  // Transférer le stock en transit vers le stock maison
  const transferTransitToHome = (id) => {
    const item = suppliesData.find(item => item.id === id);
    if (!item || item.transitQuantity <= 0) return;

    const updatedData = suppliesData.map(supply => 
      supply.id === id 
        ? { 
            ...supply, 
            homeQuantity: (supply.homeQuantity || 0) + (supply.transitQuantity || 0),
            transitQuantity: 0,
            transitOrderDate: null,
            transitSupplierId: null
          }
        : supply
    );
    
    setSuppliesData(updatedData);
    saveSuppliesData(updatedData);
    alert('Stock transféré avec succès vers le stock à la maison');
  };

  // Filtrer les données selon le terme de recherche
  const filteredData = suppliesData.filter(item => {
    const term = searchTerm.toLowerCase();
    return item.name.toLowerCase().includes(term);
  });

  return (
    <div className="supplies-stock-container">
      <h2>Stock Fournitures</h2>
      
      {/* Section d'ajout d'un nouvel article */}
      <div className="add-item-section">
        <div className="add-item-form">
          <input
            type="text"
            placeholder="Nom de la fourniture (ex: enveloppes, étiquettes...)"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            className="item-name-input"
            onKeyPress={(e) => e.key === 'Enter' && addNewItem()}
          />
          <button onClick={addNewItem} className="add-item-btn">
            Ajouter
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="table-controls">
        <input
          type="text"
          placeholder="Rechercher une fourniture..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      
      {/* Tableau des fournitures */}
      <div className="table-wrapper">
        <table className="supplies-table">
          <thead>
            <tr>
              <th>Fourniture</th>
              <th>Stock Maison</th>
              <th>Stock en Transit</th>
              <th>Stock Total</th>
              <th>Date de commande</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => {
              const totalStock = (item.homeQuantity || 0) + (item.transitQuantity || 0);
              
              return (
                <tr key={item.id} className="supply-row">
                  <td>{item.name}</td>
                  
                  {/* Stock Maison éditable */}
                  <td className="editable-cell">
                    {editingHomeStock === item.id ? (
                      <div className="edit-mode">
                        <input
                          type="number"
                          value={tempHomeStock}
                          onChange={(e) => setTempHomeStock(e.target.value)}
                          min="0"
                          className="stock-input"
                        />
                        <button 
                          onClick={() => handleHomeStockSave(item.id)}
                          className="save-btn"
                        >
                          ✓
                        </button>
                      </div>
                    ) : (
                      <div className="display-mode">
                        <span>{item.homeQuantity || 0}</span>
                        <button 
                          onClick={() => handleHomeStockEdit(item.id)}
                          className="edit-btn"
                        >
                          ✎
                        </button>
                      </div>
                    )}
                  </td>
                  
                  {/* Stock en Transit éditable */}
                  <td className="editable-cell">
                    <div className="display-mode">
                      <span>{item.transitQuantity || 0}</span>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleTransitStockEdit(item.id)}
                          className="edit-btn"
                          title="Modifier la commande"
                        >
                          ✎
                        </button>
                        {item.transitQuantity > 0 && (
                          <button 
                            onClick={() => transferTransitToHome(item.id)}
                            className="delivered-btn"
                            title="Marquer comme livré"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  {/* Stock Total */}
                  <td className="total-stock">{totalStock}</td>
                  
                  {/* Date de commande */}
                  <td>
                    {item.transitOrderDate ? new Date(item.transitOrderDate).toLocaleDateString() : '-'}
                  </td>
                  
                  {/* Actions */}
                  <td>
                    <button 
                      onClick={() => removeItem(item.id)}
                      className="remove-btn"
                      title="Supprimer cet article"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Résumé */}
      <div className="supplies-summary">
        <p>Total: {filteredData.length} fourniture(s)</p>
        <p>Stock Maison Total: {filteredData.reduce((sum, item) => sum + (item.homeQuantity || 0), 0)} unités</p>
        <p>Stock en Transit Total: {filteredData.reduce((sum, item) => sum + (item.transitQuantity || 0), 0)} unités</p>
        <p>Stock Global Total: {filteredData.reduce((sum, item) => sum + (item.homeQuantity || 0) + (item.transitQuantity || 0), 0)} unités</p>
      </div>
      
      {/* Popup d'édition du stock en transit */}
      {editingTransitStock && (
        <div className="supplier-select-overlay">
          <div className="supplier-select-popup">
            <h3>Détails de la commande en transit</h3>
            
            <div className="popup-field">
              <label>Quantité:</label>
              <input
                type="number"
                value={tempTransitStock}
                onChange={(e) => setTempTransitStock(e.target.value)}
                min="0"
                className="popup-input"
              />
            </div>
            
            <div className="popup-field">
              <label>Date de commande:</label>
              <input
                type="date"
                value={tempTransitDate}
                onChange={(e) => setTempTransitDate(e.target.value)}
                className="popup-input date-input"
              />
            </div>
            
            <div className="popup-field">
              <label>Fournisseur (optionnel):</label>
              <input
                type="text"
                value={tempSupplierId}
                onChange={(e) => setTempSupplierId(e.target.value)}
                placeholder="Nom du fournisseur"
                className="popup-input"
              />
            </div>
            
            <div className="supplier-select-buttons">
              <button onClick={() => setEditingTransitStock(null)} className="cancel-btn">
                Annuler
              </button>
              <button 
                onClick={() => handleTransitStockSave(editingTransitStock)}
                className="save-btn"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuppliesStock;